import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useSidebar } from '../../context/SidebarContext';
import clientService from '../../services/clientService';
import invoiceService from '../../services/invoiceService';
import notificationService from '../../services/notificationService';
import orderService from '../../services/orderService';
import {
  GlobeIcon,
  SearchIcon,
  CloseIcon,
  ClientsIcon,
  OrdersIcon,
  InvoicesIcon,
  NotificationIcon,
  HelpIcon,
  AlertIcon
} from '../common/SvgIcons';
import './TopHeader.css';

const TopHeader = () => {
  const { user, logout } = useAuth();
  const { toggleSidebar, isOpen } = useSidebar();
  const { t, language, toggleLanguage, isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboardRoute = location.pathname === '/' || location.pathname.startsWith('/dashboard');
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [mobileSearchActive, setMobileSearchActive] = useState(false);

  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const notificationRef = useRef(null);
  const helpRef = useRef(null);
  const profileRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const notificationPollIntervalRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (helpRef.current && !helpRef.current.contains(event.target)) {
        setShowHelp(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format time ago helper
  const formatTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInSeconds = Math.floor((now - notificationDate) / 1000);

    if (diffInSeconds < 60) {
      return t('time.justNow', 'Just now');
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${t('time.mAgo', 'min ago')}`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${t('time.hAgo', 'hours ago')}`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${t('time.dAgo', 'days ago')}`;
    } else {
      return notificationDate.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US');
    }
  };

  const getTranslatedTitle = (title) => {
    if (!title) return '';
    const map = {
      'User Deleted': t('notification.userDeleted', 'User Deleted'),
      'User Created': t('notification.userCreated', 'User Created'),
      'Supplier Deleted': t('notification.supplierDeleted', 'Supplier Deleted'),
      'Supplier Created': t('notification.supplierCreated', 'Supplier Created'),
      'New Supplier Added': t('notification.supplierCreated', 'Supplier Created'),
      'Supplier Updated': t('notification.supplierUpdated', 'Supplier Updated'),
      'Material Deleted': t('notification.materialDeleted', 'Material Deleted'),
      'Material Created': t('notification.materialCreated', 'Material Created'),
      'New Material Added': t('notification.materialCreated', 'Material Created'),
      'Material Updated': t('notification.materialUpdated', 'Material Updated'),
      'Material Stock Low': t('notification.lowStockAlert', 'Low Stock Alert'),
      'Order Created': t('notification.orderCreated', 'Order Created'),
      'Order Status Changed': t('notification.orderStatusChanged', 'Order Status Changed'),
      'Order Status Updated': t('notification.orderStatusUpdated', 'Order Status Updated'),
      'Order Updated': t('notification.orderUpdated', 'Order Updated'),
      'Order Deleted': t('notification.orderDeleted', 'Order Deleted'),
      'Invoice Created': t('notification.invoiceCreated', 'Invoice Created'),
      'Invoice Updated': t('notification.invoiceUpdated', 'Invoice Updated'),
      'Payment Received': t('notification.paymentReceived', 'Payment Received'),
      'Low Stock Alert': t('notification.lowStockAlert', 'Low Stock Alert'),
      'System Alert': t('notification.systemAlert', 'System Alert'),
      'Client Created': t('notification.clientCreated', 'Client Created'),
      'New Client Added': t('notification.clientCreated', 'Client Created'),
      'Client Updated': t('notification.clientUpdated', 'Client Updated'),
      'Client Deleted': t('notification.clientDeleted', 'Client Deleted'),
      'Purchase Created': t('notification.purchaseCreated', 'Purchase Created'),
      'Purchase Order Created': t('notification.purchaseCreated', 'Purchase Order Created')
    };
    return map[title] || title;
  };

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return;

    try {
      setLoadingNotifications(true);
      const response = await notificationService.getNotifications({ limit: 50 });
      if (response.success) {
        // Backend returns: { success, data: { notifications, unreadCount, pagination } }
        setNotifications(response.data?.notifications || []);
        setUnreadCount(response.data?.unreadCount || 0);
      }
    } catch (error) {
      // Silently handle 404 - endpoint might not exist yet or server needs restart
      if (error.response?.status !== 404) {
        console.error('Error fetching notifications:', error);
      }
      // Set empty state on error
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoadingNotifications(false);
    }
  }, [user]);

  // Fetch unread count only (lighter request)
  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;

    try {
      const response = await notificationService.getUnreadCount();
      if (response.success) {
        setUnreadCount(response.unreadCount || 0);
      }
    } catch (error) {
      // Silently handle 404 - endpoint might not exist yet or server needs restart
      if (error.response?.status !== 404) {
        console.error('Error fetching unread count:', error);
      }
      setUnreadCount(0);
    }
  }, [user]);

  // Initial fetch and polling with visibility detection
  useEffect(() => {
    if (user) {
      fetchNotifications();

      // Poll for unread count every 3 minutes (reduced from 30 seconds)
      notificationPollIntervalRef.current = setInterval(() => {
        // Only poll if tab is visible
        if (!document.hidden) {
          fetchUnreadCount();
        }
      }, 180000); // 3 minutes = 180000ms
    }

    return () => {
      if (notificationPollIntervalRef.current) {
        clearInterval(notificationPollIntervalRef.current);
      }
    };
  }, [user, fetchNotifications, fetchUnreadCount]);

  // Pause/resume polling based on tab visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        // Tab became visible, fetch latest notifications
        fetchUnreadCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, fetchUnreadCount]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (notificationPollIntervalRef.current) {
        clearInterval(notificationPollIntervalRef.current);
      }
    };
  }, []);

  // Search functionality with proper error handling
  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
      setSearchError(null);
      return;
    }

    // Cancel previous search if still in progress
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this search
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setIsSearching(true);
    setSearchError(null);

    try {
      const searchQuery = query.trim();

      // Make parallel requests with abort signal support
      const canSearchClients = ['receptionist', 'designer', 'financial', 'admin'].includes(user?.role);

      const [clientsRes, ordersRes, invoicesRes] = await Promise.allSettled([
        canSearchClients
          ? clientService.getClients({ search: searchQuery }).catch(err => {
            if (err.name === 'AbortError' || signal.aborted) throw err;
            return { error: err };
          })
          : Promise.resolve({ data: [] }),
        orderService.getOrders({ search: searchQuery }).catch(err => {
          if (err.name === 'AbortError' || signal.aborted) throw err;
          return { error: err };
        }),
        invoiceService.getInvoices({ search: searchQuery }).catch(err => {
          if (err.name === 'AbortError' || signal.aborted) throw err;
          return { error: err };
        }),
      ]);

      // Check if search was aborted
      if (signal.aborted) {
        return;
      }

      const results = [];
      let hasRateLimitError = false;
      let rateLimitMessage = '';

      // Process clients
      if (clientsRes.status === 'fulfilled' && !clientsRes.value?.error) {
        if (clientsRes.value?.data) {
          const clients = Array.isArray(clientsRes.value.data) ? clientsRes.value.data : [];
          clients.slice(0, 3).forEach(client => {
            results.push({
              id: client._id,
              type: 'client',
              title: client.name || client.factoryName,
              subtitle: client.phone || '',
              path: `/clients`,
            });
          });
        }
      } else if (clientsRes.status === 'rejected' || clientsRes.value?.error) {
        const error = clientsRes.reason || clientsRes.value?.error;
        if (error?.response?.status === 429 || error?.response?.data?.error === 'RATE_LIMIT_EXCEEDED') {
          hasRateLimitError = true;
          rateLimitMessage = error.response?.data?.message || 'Rate limit exceeded. Please wait a moment.';
        }
      }

      // Process orders
      if (ordersRes.status === 'fulfilled' && !ordersRes.value?.error) {
        if (ordersRes.value?.data) {
          const orders = Array.isArray(ordersRes.value.data) ? ordersRes.value.data : [];
          orders.slice(0, 3).forEach(order => {
            results.push({
              id: order._id,
              type: 'order',
              title: `Order #${order.orderNumber || order._id.slice(-6)}`,
              subtitle: order.client?.name || 'Unknown Client',
              path: `/orders`,
            });
          });
        }
      } else if (ordersRes.status === 'rejected' || ordersRes.value?.error) {
        const error = ordersRes.reason || ordersRes.value?.error;
        if (error?.response?.status === 429 || error?.response?.data?.error === 'RATE_LIMIT_EXCEEDED') {
          hasRateLimitError = true;
          rateLimitMessage = error.response?.data?.message || 'Rate limit exceeded. Please wait a moment.';
        }
      }

      // Process invoices
      if (invoicesRes.status === 'fulfilled' && !invoicesRes.value?.error) {
        if (invoicesRes.value?.data) {
          const invoices = Array.isArray(invoicesRes.value.data) ? invoicesRes.value.data : [];
          invoices.slice(0, 3).forEach(invoice => {
            results.push({
              id: invoice._id,
              type: 'invoice',
              title: `Invoice #${invoice.invoiceNumber || invoice._id.slice(-6)}`,
              subtitle: invoice.client?.name || 'Unknown Client',
              path: `/invoices`,
            });
          });
        }
      } else if (invoicesRes.status === 'rejected' || invoicesRes.value?.error) {
        const error = invoicesRes.reason || invoicesRes.value?.error;
        if (error?.response?.status === 429 || error?.response?.data?.error === 'RATE_LIMIT_EXCEEDED') {
          hasRateLimitError = true;
          rateLimitMessage = error.response?.data?.message || 'Rate limit exceeded. Please wait a moment.';
        }
      }

      if (hasRateLimitError) {
        setSearchError(rateLimitMessage);
        setSearchResults([]);
        setShowSearchResults(true);
      } else {
        setSearchResults(results);
        setShowSearchResults(true);
        setSearchError(null);
      }
    } catch (error) {
      if (error.name === 'AbortError' || signal.aborted) {
        return; // Search was cancelled, ignore error
      }

      console.error('Search error:', error);
      if (error?.response?.status === 429) {
        setSearchError(error.response?.data?.message || 'Too many requests. Please wait a moment before searching again.');
      } else {
        setSearchError('Search failed. Please try again.');
      }
      setSearchResults([]);
      setShowSearchResults(true);
    } finally {
      if (!signal.aborted) {
        setIsSearching(false);
      }
    }
  }, []);

  const handleSearchClick = (e) => {
    if (window.innerWidth <= 768 && !mobileSearchActive) {
      e.preventDefault();
      setMobileSearchActive(true);
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  };

  const closeMobileSearch = () => {
    setMobileSearchActive(false);
    setSearchValue('');
    setShowSearchResults(false);
  };

  // Debounced search handler
  const handleSearch = useCallback((e) => {
    e?.preventDefault();
    const query = e?.target?.value || searchValue;

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Cancel any in-progress search
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
      setSearchError(null);
      return;
    }

    // Debounce: wait 500ms after user stops typing
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, 500);
  }, [searchValue, performSearch]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);

    if (!value.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
      setSearchError(null);
      // Clear any pending search
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      return;
    }

    // Trigger debounced search
    handleSearch(e);
  };

  const handleResultClick = (result) => {
    setSearchValue('');
    setShowSearchResults(false);
    navigate(result.path);
  };

  const handleNotificationClick = async () => {
    const wasOpen = showNotifications;
    setShowNotifications(!showNotifications);
    setShowHelp(false);
    setShowProfileMenu(false);

    // Fetch notifications when opening dropdown
    if (!wasOpen) {
      await fetchNotifications();
    }
  };

  const handleMarkAsRead = async (notificationId, e) => {
    e?.stopPropagation();
    try {
      const response = await notificationService.markAsRead(notificationId);
      if (response.success) {
        setUnreadCount(response.unreadCount || 0);
        setNotifications(prev =>
          prev.map(n => n._id === notificationId ? { ...n, read: true, readAt: new Date() } : n)
        );
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async (e) => {
    e?.stopPropagation();
    try {
      const response = await notificationService.markAllAsRead();
      if (response.success) {
        setUnreadCount(0);
        setNotifications(prev =>
          prev.map(n => ({ ...n, read: true, readAt: new Date() }))
        );
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationItemClick = (notification) => {
    // Mark as read if unread
    if (!notification.read) {
      handleMarkAsRead(notification._id);
    }

    // Navigate if actionUrl exists
    if (notification.actionUrl) {
      setShowNotifications(false);
      navigate(notification.actionUrl);
    } else {
      setShowNotifications(false);
    }
  };

  const handleHelpClick = () => {
    setShowHelp(!showHelp);
    setShowNotifications(false);
    setShowProfileMenu(false);
  };

  const handleProfileClick = () => {
    setShowProfileMenu(!showProfileMenu);
    setShowNotifications(false);
    setShowHelp(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="top-header">
      <div className="header-content">
        {/* Mobile Menu Button */}
        <button
          className="mobile-menu-btn"
          onClick={toggleSidebar}
          aria-label="Toggle menu"
          type="button"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            {isOpen ? (
              // Close icon (X)
              <>
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </>
            ) : (
              // Hamburger icon
              <>
                <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </>
            )}
          </svg>
        </button>

        {/* Search Bar */}
        <form
          onSubmit={handleSearch}
          className={`header-search ${mobileSearchActive ? 'mobile-active' : ''}`}
          ref={searchRef}
        >
          <button
            type="submit"
            className="search-icon-btn"
            onClick={handleSearchClick}
          >
            <SearchIcon className="w-4 h-4 text-gray-500" />
          </button>
          <input
            ref={searchInputRef}
            type="text"
            placeholder={t('header.searchPlaceholder')}
            value={searchValue}
            onChange={handleSearchChange}
            onFocus={() => {
              if (searchResults.length > 0 || searchError || isSearching) {
                setShowSearchResults(true);
              }
            }}
            className="search-input"
            disabled={isSearching}
          />

          {/* Close Button for Mobile */}
          {mobileSearchActive && (
            <button
              type="button"
              className="search-close-btn"
              onClick={(e) => {
                e.preventDefault();
                closeMobileSearch();
              }}
            >
              <CloseIcon className="w-4 h-4" />
            </button>
          )}

          {/* Loading indicator */}
          {isSearching && (
            <div className="search-loading">
              <div className="search-spinner"></div>
            </div>
          )}

          {/* Search Results Dropdown */}
          {showSearchResults && (
            <div className="search-results-dropdown">
              {searchError ? (
                <div className="search-error">
                  <div className="search-error-icon"><AlertIcon className="w-5 h-5 text-amber-500" /></div>
                  <div className="search-error-message">{searchError}</div>
                </div>
              ) : isSearching ? (
                <div className="search-loading-state">
                  <div className="search-spinner-small"></div>
                  <span>{t('header.searching')}</span>
                </div>
              ) : searchResults.length > 0 ? (
                <div role="listbox" aria-label={t('action.search')}>
                  <div className="search-results-header">{t('action.search')}</div>
                  {searchResults.map((result) => (
                    <div
                      key={`${result.type}-${result.id}`}
                      className="search-result-item"
                      role="option"
                      tabIndex={0}
                      aria-selected="false"
                      onClick={() => handleResultClick(result)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleResultClick(result);
                        }
                      }}
                    >
                      <div className="search-result-icon">
                        {result.type === 'client' && <ClientsIcon className="w-5 h-5 text-blue-600" />}
                        {result.type === 'order' && <OrdersIcon className="w-5 h-5 text-indigo-600" />}
                        {result.type === 'invoice' && <InvoicesIcon className="w-5 h-5 text-emerald-600" />}
                      </div>
                      <div className="search-result-content">
                        <div className="search-result-title">{result.title}</div>
                        <div className="search-result-subtitle">{result.subtitle}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : searchValue.trim() ? (
                <div className="search-no-results">{t('header.noResults')}</div>
              ) : null}
            </div>
          )}
        </form>

        {/* Right Side Actions */}
        <div className="top-header-actions flex items-center gap-3">

          {/* Bilingual Language Switcher Button */}
          <button
            type="button"
            className="language-switch-btn flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-bold transition shadow-sm"
            style={{
              background: 'var(--surface, #fff)',
              borderColor: 'rgba(var(--theme-primary-rgb, 5, 150, 105), 0.35)',
              color: 'var(--text-primary, #0f172a)'
            }}
            onClick={toggleLanguage}
            title={language === 'ar' ? 'Switch interface to English' : 'تحويل الواجهة إلى العربية'}
          >
            <GlobeIcon className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--theme-primary)' }} />
            <span style={{ color: 'var(--theme-primary)', fontWeight: '700' }}>{language === 'ar' ? 'English' : 'العربية'}</span>
          </button>

          {/* Help Icon */}
          <div className="header-dropdown-wrapper" ref={helpRef}>
            <button
              className="header-icon-btn"
              title={t('header.help')}
              onClick={handleHelpClick}
              aria-haspopup="true"
              aria-expanded={showHelp}
              aria-label={t('header.help')}
            >
              <HelpIcon className="w-5 h-5" />
            </button>
            {showHelp && (
              <div className="help-dropdown" role="menu">
                <div className="help-dropdown-header">{t('header.help')}</div>
                <div className="help-dropdown-item" role="menuitem" tabIndex={0} onClick={() => { setShowHelp(false); navigate('/dashboard'); }}>
                  <span>{t('header.dashboardGuide', 'Dashboard Guide')}</span>
                </div>
                <div className="help-dropdown-item" role="menuitem" tabIndex={0} onClick={() => { setShowHelp(false); window.open('mailto:support@sendroli.com', '_blank'); }}>
                  <span>{t('header.contactSupport', 'Contact Support')}</span>
                </div>
                <div className="help-dropdown-item" role="menuitem" tabIndex={0} onClick={() => { setShowHelp(false); alert(t('header.shortcutsHelp', 'Keyboard Shortcuts:\n\nCtrl+K / Cmd+K - Search\nEsc - Close dialogs')); }}>
                  <span>{t('header.shortcuts', 'Keyboard Shortcuts')}</span>
                </div>
              </div>
            )}
          </div>

          {/* Notifications Icon */}
          <div className="header-dropdown-wrapper" ref={notificationRef}>
            <button
              className="header-icon-btn notification-btn"
              title={t('header.notifications')}
              onClick={handleNotificationClick}
              aria-haspopup="true"
              aria-expanded={showNotifications}
              aria-label={t('header.notifications')}
            >
              <NotificationIcon className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </button>
            {showNotifications && (
              <div className="notification-dropdown" role="region" aria-label={t('header.notifications')}>
                <div className="notification-dropdown-header">
                  <span>{t('header.notifications')}</span>
                  {unreadCount > 0 && (
                    <span
                      className="notification-mark-all"
                      onClick={handleMarkAllAsRead}
                      style={{ cursor: 'pointer' }}
                    >
                      {t('notification.markAllAsRead', 'Mark all as read')}
                    </span>
                  )}
                </div>
                <div className="notification-list">
                  {loadingNotifications ? (
                    <div className="notification-empty">{t('common.loading', 'Loading...')}</div>
                  ) : notifications.length > 0 ? (
                    notifications.map((notification) => (
                      <div
                        key={notification._id}
                        className={`notification-item ${!notification.read ? 'unread' : ''}`}
                        onClick={() => handleNotificationItemClick(notification)}
                      >
                        <div className="notification-item-content">
                          <div className="notification-item-title">{getTranslatedTitle(notification.title)}</div>
                          <div className="notification-item-message">{notification.message}</div>
                          <div className="notification-item-time">
                            {formatTimeAgo(notification.createdAt)}
                          </div>
                        </div>
                        {!notification.read && <div className="notification-dot"></div>}
                      </div>
                    ))
                  ) : (
                    <div className="notification-empty">{t('notification.noNotifications', 'No notifications')}</div>
                  )}
                </div>
                <div className="notification-dropdown-footer">
                  <button onClick={() => { setShowNotifications(false); navigate('/notifications'); }}>
                    {t('notification.viewAll', 'View All Notifications')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Profile Avatar */}
          <div className="profile-dropdown" ref={profileRef}>
            <button 
              className="profile-btn" 
              onClick={handleProfileClick}
              aria-haspopup="true"
              aria-expanded={showProfileMenu}
              aria-label="User Profile Menu"
            >
              <div className="profile-avatar">
                {user?.fullName?.charAt(0).toUpperCase() || 'U'}
              </div>
            </button>
            {showProfileMenu && (
              <div className="profile-menu" role="menu">
                <div className="profile-menu-header">
                  <div className="profile-menu-name">{user?.fullName || 'User'}</div>
                  <div className="profile-menu-email">{user?.email || ''}</div>
                  <div className="profile-menu-role">{t(`role.${user?.role}`, user?.role || '')}</div>
                </div>
                <div className="profile-menu-divider"></div>
                <div className="profile-menu-item" onClick={() => { setShowProfileMenu(false); navigate('/dashboard'); }}>
                  <span>{t('nav.dashboard', 'Dashboard')}</span>
                </div>
                <div className="profile-menu-item" onClick={() => { setShowProfileMenu(false); navigate('/settings'); }}>
                  <span>{t('nav.settings', 'Settings')}</span>
                </div>
                <div className="profile-menu-divider"></div>
                <div className="profile-menu-item logout-item" onClick={handleLogout}>
                  <span>{t('nav.logout', 'Logout')}</span>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </header>
  );
};

export default TopHeader;

